import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    getMieChiusure,
    createMiaChiusura,
    updateMiaChiusura,
    deleteMiaChiusura,
    adminGetChiusureFornitore,
    adminCreateChiusuraFornitore,
    adminUpdateChiusuraFornitore,
    adminDeleteChiusuraFornitore,
} from "../Admin/adminApi";

import InlineDatePicker from "./InlineDatePicker";

const emptyForm = {
    data_inizio: "",
    data_fine: "",
    chiude_ritiro: true,
    chiude_consegna: true,
    nascondi_in_ricerca: false,
    note: "",
};

function formatScope(row) {
    if (row.chiude_ritiro && row.chiude_consegna) return "Ritiro + consegna";
    if (row.chiude_ritiro) return "Solo ritiro";
    if (row.chiude_consegna) return "Solo consegna";
    return "—";
}

function normalizeToIsoDateOnly(value) {
    if (!value) return "";

    // già YYYY-MM-DD
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
        return value.trim();
    }

    // formato italiano dd/mm/yyyy -> yyyy-mm-dd
    if (typeof value === "string") {
        const it = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (it) {
            const [, dd, mm, yyyy] = it;
            return `${yyyy}-${mm}-${dd}`;
        }
    }

    // ISO / Date / timestamp
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function formatDateIt(value) {
    const iso = normalizeToIsoDateOnly(value);
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
}

function isoToItDate(value) {
    const iso = normalizeToIsoDateOnly(value);
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
}

function itToIsoDate(value) {
    const raw = String(value || "").trim();
    const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;

    const [, dd, mm, yyyy] = m;
    const iso = `${yyyy}-${mm}-${dd}`;

    const dt = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return null;

    if (
        dt.getFullYear() !== Number(yyyy) ||
        dt.getMonth() + 1 !== Number(mm) ||
        dt.getDate() !== Number(dd)
    ) {
        return null;
    }

    return iso;
}

export default function FornitoreChiusure({
    token,
    ui,
    mode = "self",
    fornitoreId = null,
    title = "Periodi di chiusura",
}) {
    const isAdminMode = mode === "admin";

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const canLoad = useMemo(() => {
        if (!token) return false;
        if (!isAdminMode) return true;
        return Number.isFinite(Number(fornitoreId)) && Number(fornitoreId) > 0;
    }, [token, isAdminMode, fornitoreId]);

    const loadRows = async () => {
        if (!canLoad) return;
        try {
            setLoading(true);
            const data = isAdminMode
                ? await adminGetChiusureFornitore(fornitoreId, token)
                : await getMieChiusure(token);
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toast.error("Errore caricamento chiusure");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canLoad, isAdminMode, fornitoreId, token]);

    const resetForm = () => {
        setEditingId(null);
        setForm(emptyForm);
    };

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const validate = () => {
        const startIso = normalizeToIsoDateOnly(form.data_inizio);
        const endIso = normalizeToIsoDateOnly(form.data_fine);

        if (!startIso || !endIso) {
            toast.error("Inserisci data inizio e data fine");
            return null;
        }

        if (endIso < startIso) {
            toast.error("La data fine deve essere uguale o successiva alla data inizio");
            return null;
        }

        if (!form.chiude_ritiro && !form.chiude_consegna) {
            toast.error("Seleziona almeno ritiro o consegna");
            return null;
        }

        return { startIso, endIso };
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        const valid = validate();
        if (!valid) return;

        const payload = {
            data_inizio: valid.startIso,
            data_fine: valid.endIso,
            chiude_ritiro: !!form.chiude_ritiro,
            chiude_consegna: !!form.chiude_consegna,
            nascondi_in_ricerca: !!form.nascondi_in_ricerca,
            note: (form.note || "").trim() || null,
        };

        try {
            setSaving(true);

            let saved;
            if (editingId) {
                saved = isAdminMode
                    ? await adminUpdateChiusuraFornitore(
                        fornitoreId,
                        editingId,
                        payload,
                        token
                    )
                    : await updateMiaChiusura(editingId, payload, token);

                setRows((prev) =>
                    prev
                        .map((r) => (Number(r.id) === Number(editingId) ? saved : r))
                        .sort((a, b) =>
                            String(a.data_inizio).localeCompare(String(b.data_inizio))
                        )
                );

                toast.success("Periodo aggiornato");
            } else {
                saved = isAdminMode
                    ? await adminCreateChiusuraFornitore(fornitoreId, payload, token)
                    : await createMiaChiusura(payload, token);

                setRows((prev) =>
                    [...prev, saved].sort((a, b) =>
                        String(a.data_inizio).localeCompare(String(b.data_inizio))
                    )
                );

                toast.success("Periodo creato");
            }

            resetForm();
        } catch (e) {
            console.error(e);
            toast.error(e.message || "Operazione non riuscita");
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (row) => {
        setEditingId(row.id);
        setForm({
            data_inizio: normalizeToIsoDateOnly(row.data_inizio || ""),
            data_fine: normalizeToIsoDateOnly(row.data_fine || ""),
            chiude_ritiro: !!row.chiude_ritiro,
            chiude_consegna: !!row.chiude_consegna,
            nascondi_in_ricerca: !!row.nascondi_in_ricerca,
            note: row.note || "",
        });
    };

    const handleDelete = async (row) => {
        if (!window.confirm("Eliminare questo periodo di chiusura?")) return;

        try {
            if (isAdminMode) {
                await adminDeleteChiusuraFornitore(fornitoreId, row.id, token);
            } else {
                await deleteMiaChiusura(row.id, token);
            }

            setRows((prev) => prev.filter((r) => Number(r.id) !== Number(row.id)));

            if (Number(editingId) === Number(row.id)) {
                resetForm();
            }

            toast.success("Periodo eliminato");
        } catch (e) {
            console.error(e);
            toast.error(e.message || "Errore eliminazione");
        }
    };

    if (!canLoad) return null;

    return (
        <div>
            <div style={{ marginBottom: 14 }}>
                <h4 className={ui.cardTitle} style={{ marginBottom: 6 }}>
                    {title}
                </h4>
                <div className={ui.hint}>
                    Imposta periodi in cui il fornitore non effettua ritiro e/o consegna.
                    Puoi anche decidere se nasconderlo dalla ricerca mentre la chiusura è
                    attiva.
                </div>
            </div>

            <form onSubmit={handleSubmit} style={{ marginBottom: 18 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 12,
                        alignItems: "end",
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <label className={ui.label}>Data inizio</label>
                        <InlineDatePicker
                            value={normalizeToIsoDateOnly(form.data_inizio)}
                            onChange={(next) => setField("data_inizio", next)}
                            placeholder="gg/mm/aaaa"
                        />
                    </div>

                    <div>
                        <label className={ui.label}>Data fine</label>
                        <InlineDatePicker
                            value={normalizeToIsoDateOnly(form.data_fine)}
                            onChange={(next) => setField("data_fine", next)}
                            placeholder="gg/mm/aaaa"
                            minDate={normalizeToIsoDateOnly(form.data_inizio)}
                        />
                    </div>

                    <div>
                        <label className={ui.label}>Nota (opzionale)</label>
                        <input
                            type="text"
                            className={ui.input}
                            placeholder="es. ferie estive"
                            value={form.note}
                            onChange={(e) => setField("note", e.target.value)}
                        />
                    </div>
                </div>

                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 16,
                        alignItems: "center",
                        marginBottom: 14,
                    }}
                >
                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                            type="checkbox"
                            checked={form.chiude_ritiro}
                            onChange={(e) => setField("chiude_ritiro", e.target.checked)}
                        />
                        <span>Blocca ritiro</span>
                    </label>

                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                            type="checkbox"
                            checked={form.chiude_consegna}
                            onChange={(e) => setField("chiude_consegna", e.target.checked)}
                        />
                        <span>Blocca consegna</span>
                    </label>

                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                            type="checkbox"
                            checked={form.nascondi_in_ricerca}
                            onChange={(e) =>
                                setField("nascondi_in_ricerca", e.target.checked)
                            }
                        />
                        <span>Nascondi dalla ricerca durante la chiusura</span>
                    </label>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button type="submit" className={ui.btnPrimary} disabled={saving}>
                        {saving
                            ? "Salvataggio..."
                            : editingId
                                ? "Salva modifiche"
                                : "Aggiungi chiusura"}
                    </button>

                    {(editingId ||
                        form.data_inizio ||
                        form.data_fine ||
                        form.note ||
                        !form.chiude_ritiro ||
                        !form.chiude_consegna ||
                        form.nascondi_in_ricerca) && (
                            <button
                                type="button"
                                className={ui.btnSecondary}
                                onClick={resetForm}
                                disabled={saving}
                            >
                                Annulla
                            </button>
                        )}
                </div>
            </form>

            {loading ? (
                <div className={ui.hint}>Caricamento chiusure...</div>
            ) : rows.length === 0 ? (
                <div className={ui.hint}>Nessun periodo di chiusura configurato</div>
            ) : (
                <>
                    <div className={ui.tableDesktop}>
                        <div className={ui.tableWrap}>
                            <table className={ui.table}>
                                <colgroup>
                                    <col className={ui.colClosurePeriod} />
                                    <col className={ui.colClosureScope} />
                                    <col className={ui.colClosureSearch} />
                                    <col className={ui.colClosureNote} />
                                    <col className={ui.colClosureActions} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className={`${ui.th} ${ui.thClosurePeriod}`}>Periodo</th>
                                        <th className={`${ui.th} ${ui.thClosureScope}`}>Blocco</th>
                                        <th className={`${ui.th} ${ui.thClosureSearch}`}>Ricerca</th>
                                        <th className={`${ui.th} ${ui.thClosureNote}`}>Nota</th>
                                        <th className={`${ui.th} ${ui.thClosureActions}`}>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.id}>
                                            <td className={`${ui.td} ${ui.tdClosurePeriod}`}>
                                                {formatDateIt(row.data_inizio)} → {formatDateIt(row.data_fine)}
                                            </td>

                                            <td className={`${ui.td} ${ui.tdClosureScope}`}>
                                                {formatScope(row)}
                                            </td>

                                            <td className={`${ui.td} ${ui.tdClosureSearch}`}>
                                                {row.nascondi_in_ricerca
                                                    ? "Nascosto dalla ricerca"
                                                    : "Visibile con eventuale badge"}
                                            </td>

                                            <td className={`${ui.td} ${ui.tdClosureNote}`}>
                                                {row.note || "—"}
                                            </td>

                                            <td className={`${ui.td} ${ui.tdClosureActions}`}>
                                                <div className={ui.actions}>
                                                    <button
                                                        type="button"
                                                        className={ui.btnSecondary}
                                                        onClick={() => startEdit(row)}
                                                    >
                                                        Modifica
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={ui.btnDanger}
                                                        onClick={() => handleDelete(row)}
                                                    >
                                                        Elimina
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className={ui.mobileList}>
                        {rows.map((row) => (
                            <article key={row.id} className={ui.mobileCard}>
                                <div className={ui.mobileCardTop}>
                                    <div className={ui.mobileTitleWrap}>
                                        <h4 className={ui.mobileTitle}>
                                            {formatDateIt(row.data_inizio)} → {formatDateIt(row.data_fine)}
                                        </h4>
                                        <p className={ui.mobileSub}>{formatScope(row)}</p>
                                    </div>

                                    <span
                                        className={`${ui.mobileBadge} ${row.nascondi_in_ricerca
                                                ? ui.mobileBadgeOff
                                                : ui.mobileBadgeOn
                                            }`}
                                    >
                                        {row.nascondi_in_ricerca ? "Nascosto" : "Visibile"}
                                    </span>
                                </div>

                                <div className={ui.mobileStats}>
                                    <div className={ui.mobileStat}>
                                        <span className={ui.mobileStatLabel}>Blocco</span>
                                        <span className={ui.mobileStatValue}>{formatScope(row)}</span>
                                    </div>

                                    <div className={ui.mobileStat}>
                                        <span className={ui.mobileStatLabel}>Ricerca</span>
                                        <span className={ui.mobileStatValue}>
                                            {row.nascondi_in_ricerca ? "Nascosto" : "Visibile"}
                                        </span>
                                    </div>
                                </div>

                                <div className={ui.mobileNoteBox}>
                                    <span className={ui.mobileStatLabel}>Nota</span>
                                    <p className={ui.mobileNoteText}>{row.note || "—"}</p>
                                </div>

                                <div className={ui.mobileActions}>
                                    <button
                                        type="button"
                                        className={ui.btnSecondary}
                                        onClick={() => startEdit(row)}
                                    >
                                        Modifica
                                    </button>
                                    <button
                                        type="button"
                                        className={ui.btnDanger}
                                        onClick={() => handleDelete(row)}
                                    >
                                        Elimina
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}